import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useAsync } from '../hooks/useAsync'
import { fetchPlayerProfile, fetchPlayerStats, fetchSeasons } from '../services/league'
import SeasonPicker from '../components/SeasonPicker'
import {
  Avatar,
  Card,
  ConferenceBadge,
  EmptyState,
  Loading,
  Notice,
  PlayerCell,
  SortableHeaderRow,
  Stat,
  formatCalendarDate,
  formatPct,
  formatRecord,
  formatSharePct,
  formatSigned,
  headToHeadGamesPath,
  playerProfilePath,
  playerSeasonGamesPath,
  recordSortKey,
  RowLink,
  RowLinkTarget,
  useTableSort,
  type SortableColumn,
} from '../components/ui'
import { TeamHelmet } from '../components/TeamHelmet'
import { STAT_CATEGORIES, formatStatValue } from '../lib/statCategories'
import type {
  CoachStats,
  HeadToHeadRow,
  PlayerFinish,
  PlayerProfile as PlayerProfilePayload,
  PlayerSeasonLine,
} from '../types'
import './PlayerProfile.css'

const FINISH_LABELS: Record<PlayerFinish['place'], string> = {
  champion: 'Champion',
  runner_up: 'Runner-up',
}

/**
 * Sort key for the Finish column.
 *
 * @returns 1 for a title, 2 for a runner-up finish, and null for a season that
 * ended earlier — which groups the ordinary seasons at the end of the table in
 * both directions rather than interleaving them with the honours.
 */
function finishSortValue(finish: PlayerSeasonLine['finish']): number | null {
  if (finish === 'champion') return 1
  if (finish === 'runner_up') return 2
  return null
}

function perGame(total: number, games: number): string {
  return games > 0 ? (total / games).toFixed(1) : '—'
}

function signedPerGame(total: number, games: number): string {
  if (games === 0) return '—'
  const average = total / games
  return `${average > 0 ? '+' : ''}${average.toFixed(1)}`
}

/**
 * The headline figures: career record, rate, and scoring.
 *
 * @remarks
 * Rendered only for a player with an official game. A 0-0 tile grid would look
 * like a career rather than an absence of one, which is why the caller shows an
 * empty state instead.
 */
function CareerTiles({ profile }: { profile: PlayerProfilePayload }) {
  const career = profile.career
  if (!career) return null

  const seasonsPlayed = profile.seasons.filter(season => season.gamesPlayed > 0).length

  return (
    <div className="grid-3">
      <Stat
        label="Career record"
        value={formatRecord(career.wins, career.losses, career.ties)}
        hint={`${career.gamesPlayed} games · #${career.rank} of ${career.rankedPlayers} by wins`}
      />
      <Stat
        label="Win percentage"
        value={formatPct(career.winPercentage)}
        hint={`${seasonsPlayed} ${seasonsPlayed === 1 ? 'season' : 'seasons'} played`}
      />
      <Stat
        label="Points for"
        value={career.pointsFor.toLocaleString()}
        hint={`${perGame(career.pointsFor, career.gamesPlayed)} per game`}
      />
      <Stat
        label="Points against"
        value={career.pointsAgainst.toLocaleString()}
        hint={`${perGame(career.pointsAgainst, career.gamesPlayed)} per game`}
      />
      <Stat
        label="Point differential"
        value={formatSigned(career.pointDifferential)}
        hint={`${signedPerGame(career.pointDifferential, career.gamesPlayed)} per game`}
      />
      <Stat
        label="P1 share"
        value={formatSharePct(career.gamesPlayed === 0 ? null : career.p1Percentage)}
        hint={`${career.p1Games} of ${career.gamesPlayed} games with the controller`}
      />
    </div>
  )
}

/** The player's championship series appearances, newest first. */
function Finishes({ finishes }: { finishes: PlayerFinish[] }) {
  return (
    <ul className="finish-list">
      {finishes.map(finish => (
        <li key={`${finish.seasonNumber}-${finish.place}`} className={`finish finish--${finish.place}`}>
          <Link className="finish__season" to={`/historical/${finish.seasonNumber}`}>
            <span className="finish__season-label">Season</span>
            <span className="finish__season-number">{finish.seasonNumber}</span>
          </Link>

          <div className="finish__body">
            <div className="finish__place">
              {FINISH_LABELS[finish.place]}
              {finish.teamAbbreviation && (
                <TeamHelmet abbreviation={finish.teamAbbreviation} size="sm" />
              )}
            </div>
            <div className="finish__detail">
              {finish.opponentName
                ? `${finish.place === 'champion' ? 'Beat' : 'Lost to'} ${finish.opponentName}`
                : 'Other finalist unrecorded'}
              {finish.seriesResult && ` · ${finish.seriesResult}`}
            </div>
            {finish.decidedOn && (
              <div className="finish__date">{formatCalendarDate(finish.decidedOn)}</div>
            )}
          </div>
        </li>
      ))}
    </ul>
  )
}

/**
 * Every season the player took part in.
 *
 * @param leaguePlayerId - Whose table this is, so a season number can open that
 * player's results for it.
 */
function SeasonTable({
  rows,
  leaguePlayerId,
}: {
  rows: PlayerSeasonLine[]
  leaguePlayerId: number
}) {
  const columns = useMemo<Array<SortableColumn<PlayerSeasonLine>>>(
    () => [
      { key: 'season', label: 'Season', className: 'num', sort: row => row.seasonNumber },
      {
        key: 'conference',
        label: 'Conf',
        sortLabel: 'conference',
        sort: row => row.conference,
      },
      {
        key: 'gamesPlayed',
        label: 'GP',
        className: 'num',
        sortLabel: 'games played',
        sort: row => row.gamesPlayed,
      },
      {
        key: 'record',
        label: 'Record',
        className: 'record',
        hint: 'Sort by wins, then by fewest losses',
        sort: row => recordSortKey(row.wins, row.losses),
      },
      {
        key: 'pct',
        label: 'Pct',
        className: 'num',
        sortLabel: 'win percentage',
        // A season with no games renders .000 without having earned it.
        sort: row => (row.gamesPlayed === 0 ? null : row.winPercentage),
      },
      {
        key: 'pointsFor',
        label: 'PF',
        className: 'num',
        sortLabel: 'points for',
        sort: row => row.pointsFor,
      },
      {
        key: 'pointsAgainst',
        label: 'PA',
        className: 'num',
        sortLabel: 'points against',
        sort: row => row.pointsAgainst,
      },
      {
        key: 'diff',
        label: 'Diff',
        className: 'num',
        sortLabel: 'point differential',
        sort: row => row.pointDifferential,
      },
      {
        key: 'finish',
        label: 'Finish',
        hint: 'Sort titles first, then runner-up finishes',
        sort: row => finishSortValue(row.finish),
      },
    ],
    []
  )

  const sort = useTableSort(rows, columns)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={columns} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => {
            // A season reached only through a championship record has no games
            // of its own on file, so there is nothing to open.
            const gamesPath =
              row.gamesPlayed === 0
                ? null
                : playerSeasonGamesPath(leaguePlayerId, row.seasonNumber)

            return (
              <RowLink key={row.seasonNumber} to={gamesPath}>
                <td className="num">
                  <Link
                    className="season-link"
                    to={playerSeasonGamesPath(leaguePlayerId, row.seasonNumber)}
                    aria-label={`Season ${row.seasonNumber} games`}
                  >
                    {row.seasonNumber}
                  </Link>
                </td>
                <td>
                  {/* A season reached only through a championship record has no
                      membership row, which is absent rather than unassigned. */}
                  {row.conference ? (
                    <ConferenceBadge conference={row.conference} />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td className="num">{row.gamesPlayed}</td>
                <td className="record">
                  {row.gamesPlayed === 0 ? (
                    '—'
                  ) : (
                    <RowLinkTarget to={gamesPath} label={`Season ${row.seasonNumber} games`}>
                      {formatRecord(row.wins, row.losses, row.ties)}
                    </RowLinkTarget>
                  )}
                </td>
                <td className="num">
                  {row.gamesPlayed === 0 ? '—' : formatPct(row.winPercentage)}
                </td>
                <td className="num">{row.gamesPlayed === 0 ? '—' : row.pointsFor}</td>
                <td className="num">{row.gamesPlayed === 0 ? '—' : row.pointsAgainst}</td>
                <td className="num">
                  {row.gamesPlayed === 0 ? '—' : formatSigned(row.pointDifferential)}
                </td>
                <td>
                  {row.finish ? (
                    <span
                      className={`badge ${
                        row.finish === 'champion' ? 'badge--win' : 'badge--pending'
                      }`}
                    >
                      {FINISH_LABELS[row.finish]}
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
              </RowLink>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * Head-to-head record against every opponent faced.
 *
 * @param leaguePlayerId - Whose table this is, so a row can open the meetings
 * behind the record it shows.
 */
function RivalryTable({
  rows,
  leaguePlayerId,
}: {
  rows: HeadToHeadRow[]
  leaguePlayerId: number
}) {
  const columns = useMemo<Array<SortableColumn<HeadToHeadRow>>>(
    () => [
      { key: 'opponent', label: 'Opponent', sort: row => row.opponentDisplayName },
      {
        key: 'gamesPlayed',
        label: 'GP',
        className: 'num',
        sortLabel: 'games played',
        sort: row => row.gamesPlayed,
      },
      {
        key: 'record',
        label: 'Record',
        className: 'record',
        hint: 'Sort by wins, then by fewest losses',
        sort: row => recordSortKey(row.wins, row.losses),
      },
      {
        key: 'pct',
        label: 'Pct',
        className: 'num',
        sortLabel: 'win percentage',
        sort: row => (row.wins + row.ties * 0.5) / row.gamesPlayed,
      },
      {
        key: 'pointsFor',
        label: 'PF',
        className: 'num',
        sortLabel: 'points for',
        sort: row => row.pointsFor,
      },
      {
        key: 'pointsAgainst',
        label: 'PA',
        className: 'num',
        sortLabel: 'points against',
        sort: row => row.pointsAgainst,
      },
      {
        key: 'diff',
        label: 'Diff',
        className: 'num',
        sortLabel: 'point differential',
        sort: row => row.pointsFor - row.pointsAgainst,
      },
    ],
    []
  )

  const sort = useTableSort(rows, columns)

  return (
    <div className="table-wrap">
      <table className="data">
        <thead>
          <SortableHeaderRow columns={columns} sort={sort} />
        </thead>
        <tbody>
          {sort.rows.map(row => {
            const differential = row.pointsFor - row.pointsAgainst
            const tone = row.wins > row.losses ? 'win' : row.wins < row.losses ? 'loss' : 'none'
            const meetingsPath = headToHeadGamesPath(leaguePlayerId, row.opponentUserId)

            return (
              <RowLink key={row.opponentUserId} to={meetingsPath}>
                <td>
                  <PlayerCell
                    name={row.opponentDisplayName}
                    to={playerProfilePath(row.opponentUserId)}
                  />
                </td>
                <td className="num">{row.gamesPlayed}</td>
                <td className={`record rivalry--${tone}`}>
                  <RowLinkTarget
                    to={meetingsPath}
                    label={`Games against ${row.opponentDisplayName}`}
                  >
                    {formatRecord(row.wins, row.losses, row.ties)}
                  </RowLinkTarget>
                </td>
                <td className="num">
                  {formatPct((row.wins + row.ties * 0.5) / row.gamesPlayed)}
                </td>
                <td className="num">{row.pointsFor}</td>
                <td className="num">{row.pointsAgainst}</td>
                <td className="num">{formatSigned(differential)}</td>
              </RowLink>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}

/**
 * The player's own figures in each category the Stats page reports.
 *
 * @param stats - The scoped row, or null when they played nothing in scope.
 *
 * @remarks
 * Every category is on screen at once rather than behind the Stats page's
 * category buttons. Those exist to keep a seventy-row leaderboard readable; one
 * player is a handful of figures, and hiding four fifths of them behind tabs
 * would be clicks for nothing.
 *
 * A season imported from a spreadsheet has results and no box score, so a
 * player can be several games into a scope with nothing here to show. That case
 * says so rather than printing the zeros the aggregation would otherwise hand
 * over — see `gamesWithStats`.
 */
function StatFigures({ stats }: { stats: CoachStats | null }) {
  if (!stats || stats.gamesPlayed === 0) {
    return (
      <EmptyState title="No games in this scope">
        Pick another season, or all seasons, to see this player&rsquo;s numbers.
      </EmptyState>
    )
  }

  const boxScoreless = stats.gamesWithStats === 0
  const categories = boxScoreless
    ? STAT_CATEGORIES.filter(category => !category.requiresBoxScore)
    : STAT_CATEGORIES.filter(
        category => !category.requiresExtraStats || stats.gamesWithExtraStats > 0
      )

  return (
    <div className="stack">
      {boxScoreless ? (
        <Notice>
          {stats.gamesPlayed} {stats.gamesPlayed === 1 ? 'game is' : 'games are'} on record here,
          but they came from a historical spreadsheet import, which carries the score and nothing
          else. Only scoring can be shown.
        </Notice>
      ) : (
        stats.gamesWithStats < stats.gamesPlayed && (
          <Notice>
            {stats.gamesWithStats} of {stats.gamesPlayed} games in this scope carry a box score, so
            the figures below describe part of the record rather than all of it.
          </Notice>
        )
      )}

      <div className="stat-groups">
        {categories.map(category => (
          <section key={category.slug} className="stat-group">
            <h3 className="stat-group__title">{category.title}</h3>
            <dl className="stat-group__grid">
              {category.columns.map(column => (
                <div key={String(column.key)}>
                  <dt>{column.label}</dt>
                  <dd>{formatStatValue(column, stats)}</dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  )
}

/**
 * One competitor's career page.
 *
 * @remarks
 * Addressed by `league_players.id` rather than by a Discord account, because
 * most of the league's history belongs to people imported from spreadsheets who
 * never had one. A merged-away id still resolves server-side, so an old link
 * lands on the surviving profile; the URL is corrected from the response rather
 * than left claiming an identity that no longer exists.
 */
export default function PlayerProfile() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const requestedId = Number(id)

  const profile = useAsync(() => fetchPlayerProfile(requestedId), [requestedId])
  const seasons = useAsync(fetchSeasons, [])
  const [scope, setScope] = useState<number | 'all'>('all')

  const player = profile.data?.player ?? null
  const leaguePlayerId = player?.leaguePlayerId ?? null

  useEffect(() => setScope('all'), [requestedId])

  useEffect(() => {
    if (leaguePlayerId !== null && leaguePlayerId !== requestedId) {
      navigate(playerProfilePath(leaguePlayerId), { replace: true })
    }
  }, [leaguePlayerId, requestedId, navigate])

  const stats = useAsync(
    () =>
      leaguePlayerId === null
        ? Promise.resolve(null)
        : fetchPlayerStats(leaguePlayerId, scope === 'all' ? undefined : scope),
    [leaguePlayerId, scope]
  )

  // Only seasons this player took part in; a picker offering all thirty-three
  // would mostly select scopes with nothing in them.
  const playedSeasons = useMemo(() => {
    const numbers = new Set(profile.data?.seasons.map(season => season.seasonNumber) ?? [])
    return (seasons.data ?? []).filter(season => numbers.has(season.number))
  }, [seasons.data, profile.data])

  if (profile.loading) {
    return (
      <Card title="Loading profile">
        <Loading rows={6} />
      </Card>
    )
  }

  if (profile.error || !profile.data || !player) {
    return <Notice tone="error">{profile.error ?? 'That player could not be found.'}</Notice>
  }

  const { career, finishes, rivalries, seasons: seasonLines } = profile.data
  const titles = finishes.filter(finish => finish.place === 'champion').length
  const runnerUps = finishes.length - titles

  return (
    <div className="stack">
      <header className="page-header">
        <div className="profile-identity">
          <Avatar
            name={player.displayName}
            discordId={player.discordId}
            avatar={player.discordAvatar}
            size="lg"
          />
          <div>
            <span className="eyebrow">Player</span>
            <h1>{player.displayName}</h1>
            {player.aliases.length > 0 && (
              <p className="muted profile-aliases">
                Also known as {player.aliases.join(', ')}
              </p>
            )}
            {finishes.length > 0 && (
              <p className="profile-honours">
                {titles > 0 && (
                  <span className="badge badge--win">
                    {titles}× champion
                  </span>
                )}
                {runnerUps > 0 && (
                  <span className="badge badge--pending">{runnerUps}× runner-up</span>
                )}
              </p>
            )}
          </div>
        </div>
        {career && (
          <Link
            className="btn btn--ghost btn--sm"
            to={playerSeasonGamesPath(player.leaguePlayerId, 'all')}
          >
            All games
          </Link>
        )}
      </header>

      {career ? (
        <CareerTiles profile={profile.data} />
      ) : (
        <Card>
          <EmptyState title={`${player.displayName} has no games on record`}>
            Records appear here as soon as a result involving them is uploaded or imported.
          </EmptyState>
        </Card>
      )}

      {finishes.length > 0 && (
        <Card title="Finals" subtitle="Championship series appearances" flush>
          <Finishes finishes={finishes} />
        </Card>
      )}

      {seasonLines.length > 0 && (
        <Card
          title="Season by season"
          subtitle="Regular season records, matching the standings. Career totals above include the postseason."
          flush
        >
          <SeasonTable rows={seasonLines} leaguePlayerId={player.leaguePlayerId} />
        </Card>
      )}

      {rivalries.length > 0 && (
        <Card title="Rivalries" subtitle="Head-to-head against every opponent faced" flush>
          <RivalryTable rows={rivalries} leaguePlayerId={player.leaguePlayerId} />
        </Card>
      )}

      {career && (
        <Card
          title="Statistics"
          subtitle="From parsed Tecmo Super Bowl box scores"
          actions={
            playedSeasons.length > 0 && (
              <SeasonPicker
                seasons={playedSeasons}
                value={scope}
                onChange={setScope}
                includeAll
                label="Scope"
              />
            )
          }
        >
          {stats.error ? (
            <Notice tone="error">{stats.error}</Notice>
          ) : stats.loading ? (
            <Loading rows={4} />
          ) : (
            <StatFigures stats={stats.data} />
          )}
        </Card>
      )}
    </div>
  )
}
